import React, { memo, useState } from 'react';
import { Popover } from 'antd';
import { TooltipPlacement } from 'antd/lib/tooltip';

import { useStudentMiniCard } from 'core/hooks';

import { StudentMiniCard } from './StudentMiniCard';

interface IProps {
  studentId: string;
  placement?: TooltipPlacement;
  /** 'hover' for list rows / header names, 'click' for the summary strip. */
  trigger?: 'hover' | 'click';
  children: React.ReactNode;
}

/**
 * Popover wrapper that shows the student mini-card around any anchor
 * (spec 0009). The query is deferred until the popover actually opens
 * (`armed`) — the 0.4s enter delay plus the lazy skip mean a mouse
 * travelling down the chat list fires zero requests; reopening hits the
 * Apollo cache.
 */
export const StudentHoverCard = memo<IProps>(
  ({ studentId, placement = 'rightTop', trigger = 'hover', children }) => {
    const [armed, setArmed] = useState(false);
    const { card, loading, failed, refetch } = useStudentMiniCard(studentId, {
      skip: !armed,
    });

    return (
      <Popover
        content={
          <StudentMiniCard
            studentId={studentId}
            card={card}
            loading={loading || !armed}
            failed={failed}
            onRetry={refetch}
          />
        }
        placement={placement}
        trigger={trigger}
        mouseEnterDelay={0.4}
        mouseLeaveDelay={0.1}
        destroyTooltipOnHide
        onVisibleChange={(visible) => {
          if (visible) setArmed(true);
        }}
      >
        {children}
      </Popover>
    );
  },
);

StudentHoverCard.displayName = 'StudentHoverCard';
